import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import { PanelShell } from '../panel-shell.js';
import { useWatchlists } from '../../api/hooks.js';
import { getWatchlistEntities, type WatchlistEntityRow } from '../../api/client.js';
import { useActiveCountry, useSetPanelEntity, type PanelId, type TabId } from '../store.js';

// Landing-tab panel 2 of 5: Watchlist changes. Centre column (~50%).
// Shows most recently-added entries across ALL the user's watchlists
// (ordered by added_at desc). With multiple named watchlists per user,
// this panel fetches the contents of each and merges client-side.

interface MergedRow extends WatchlistEntityRow {
  watchlistId: string;
  watchlistName: string;
}

export function WatchlistChangesPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const { t } = useTranslation();
  const { data: watchlistsData, isLoading: listsLoading } = useWatchlists();
  const select = useSetPanelEntity(tabId, panelId);
  const activeCountry = useActiveCountry();

  const watchlists = watchlistsData?.data ?? [];

  // Fan out to fetch every watchlist's entities, then merge. With small
  // numbers of watchlists (10s) this is fine; if a user racks up
  // hundreds we'll need a dedicated server-side endpoint.
  const childQueries = useQueries({
    queries: watchlists.map((w) => ({
      queryKey: ['watchlists', w.id, 'entities'] as const,
      queryFn: () => getWatchlistEntities(w.id),
      staleTime: 30_000,
    })),
  });

  const isLoading = listsLoading || childQueries.some((q) => q.isLoading);
  const error = childQueries.some((q) => q.isError);

  const merged = useMemo<MergedRow[]>(() => {
    const out: MergedRow[] = [];
    childQueries.forEach((q, i) => {
      const w = watchlists[i];
      if (!w || !q.data) return;
      for (const row of q.data.data) {
        out.push({ ...row, watchlistId: w.id, watchlistName: w.name });
      }
    });
    const filtered = activeCountry ? out.filter((r) => r.countryCode === activeCountry) : out;
    filtered.sort((a, b) => b.addedAt.localeCompare(a.addedAt));
    return filtered.slice(0, 50);
  }, [childQueries, watchlists, activeCountry]);

  const showEmpty = !isLoading && !error && merged.length === 0;

  return (
    <PanelShell
      tabId={tabId}
      panelId={panelId}
      toolbar={
        <div
          className="section-header"
          style={{ padding: 'var(--s-2) var(--s-3)', marginBottom: 0 }}
        >
          {t('panels.landing.watchlistChanges.label')}
        </div>
      }
      loading={isLoading}
      error={error ? t('panels.lists.error') : false}
      empty={showEmpty ? t('panels.landing.watchlistChanges.empty') : false}
      padding="var(--s-3)"
    >
      <table className="table">
        <thead>
          <tr>
            <th>{t('panels.lists.columns.legalName')}</th>
            <th>{t('panels.lists.columns.country')}</th>
            <th>{t('panels.lists.columns.subSegment')}</th>
            <th>{t('panels.landing.watchlistChanges.watchlist')}</th>
            <th className="num">{t('panels.lists.columns.addedAt')}</th>
          </tr>
        </thead>
        <tbody>
          {merged.map((row) => (
            <tr
              key={`${row.watchlistId}:${row.id}`}
              onClick={() => select(row.id)}
              style={{ cursor: 'pointer' }}
            >
              <td>
                <strong>{row.legalName ?? '—'}</strong>
              </td>
              <td>{row.countryCode}</td>
              <td>{row.subSegment ?? '—'}</td>
              <td style={{ color: 'var(--text-tertiary)' }}>{row.watchlistName}</td>
              <td className="num" data-numeric>
                {row.addedAt.slice(0, 10)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </PanelShell>
  );
}
